import React from 'react';
import { CustomerCard } from 'containers/customers/customers.model';
import { getCardIconComponent, getCardType } from 'containers/receipts/util';
import { useAppDispatch } from 'hooks';
import { NotifierTypes } from 'constants/notifierConstants';
import { showNotification } from 'utils/Notifier';
import { CaretRightIcon } from 'assets/icons';

interface COFCardDisplayProps {
  cardData: CustomerCard;
  showCardHolderName?: boolean;
  isEditable?: boolean;
  onManageCardClick?: (card: CustomerCard) => void;
  showManageButton?: boolean;
  showChevronIcon?: boolean;
  className?: string;
}

const COFCardDisplay: React.FC<COFCardDisplayProps> = ({
  cardData,
  showCardHolderName = false,
  isEditable = false,

  onManageCardClick,
  showManageButton = false,
  showChevronIcon = false,
  className = ''
}) => {
  const dispatch = useAppDispatch();

  const formatExpiry = (month: string, year: string) => {
    return `${month.padStart(2, '0')}/${year}`;
  };

  const handleManageClick = () => {
    if (!isEditable) {
      showNotification(dispatch, NotifierTypes.WARNING, 'You don’t have permission to manage a card on file');
      return;
    }
    onManageCardClick(cardData);
  };

  const CardIcon = getCardIconComponent(cardData.network);
  const expiryDate = formatExpiry(cardData.expiryMonth.toString(), cardData.expiryYear.toString().slice(-2));

  return (
    <div className={`flex w-full items-center gap-3 ${className}`}>
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[5px] border border-borderGray p-2 ">
        <CardIcon className="path path-fill-current text-primaryText" />
      </div>
      <div className="flex grow flex-col">
        <div className="flex w-full items-center justify-between gap-2">
          <span className="text-sbase font-medium text-primaryText">
            {getCardType(cardData.network)} •••• {cardData.cardLast4}
          </span>
          {showManageButton && (
            <button
              onClick={handleManageClick}
              className="text-sbase font-semibold text-secondary hover:text-primaryButtonHover"
              aria-label="Manage card"
              tabIndex={0}>
              Manage
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 text-px13 text-accentText">
          {showCardHolderName && cardData.cardHolderName && (
            <>
              <span>{cardData.cardHolderName}</span>
              <span className="text-[#D9D9D9]">•</span>
            </>
          )}

          {!cardData.isExpired ? (
            <span>Expires {expiryDate}</span>
          ) : (
            <span className="font-medium text-error">Expired {expiryDate}</span>
          )}
        </div>
      </div>
      {showChevronIcon && (
        <button
          onClick={handleManageClick}
          className="rounded-md p-1 align-middle transition-colors hover:bg-gray-50"
          aria-label="Delete card"
          tabIndex={0}>
          <CaretRightIcon className="path-fill-current text-gray-1" />
        </button>
      )}
    </div>
  );
};

export default COFCardDisplay;
